// Browser-only image loader from a URL. Mirrors `loadImageFile` but takes
// a remote URL (Pinata / IPFS / S3 / arbitrary HTTPS) instead of a local
// `File`. Used by `renderManifest` to re-fetch the raw image at display
// time so the dither effect can be re-applied with the lib's current
// palette / params — no UI drift when the lib's defaults are tweaked.
//
// The remote server MUST send `Access-Control-Allow-Origin: *` (or your
// origin) for `getImageData` to work; otherwise the canvas tainted-data
// check fires and the load throws. Pinata's gateway sets the wildcard
// header by default.

import type { SampledImage } from '../../types';

const MAX_SAMPLE = 300;

const HISTOGRAM_STRETCH_MIN_RANGE = 0.04;

function stretchBrightnessHistogram(brightness: Float32Array): void {
  let min = 1;
  let max = 0;
  for (let i = 0; i < brightness.length; i++) {
    const v = brightness[i] ?? 0;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const range = max - min;
  if (range < HISTOGRAM_STRETCH_MIN_RANGE) return;
  const inv = 1 / range;
  for (let i = 0; i < brightness.length; i++) {
    brightness[i] = ((brightness[i] ?? 0) - min) * inv;
  }
}

function sampleImageElement(img: HTMLImageElement, dataURL: string | null): SampledImage {
  const aspect = img.width / img.height;
  let sw: number;
  let sh: number;
  if (aspect >= 1) {
    sw = Math.min(MAX_SAMPLE, img.width);
    sh = Math.round(sw / aspect);
  } else {
    sh = Math.min(MAX_SAMPLE, img.height);
    sw = Math.round(sh * aspect);
  }
  const c = document.createElement('canvas');
  c.width = sw;
  c.height = sh;
  const cx = c.getContext('2d');
  if (cx === null) throw new Error('Could not acquire 2d context for sampling canvas');
  cx.drawImage(img, 0, 0, sw, sh);
  const id = cx.getImageData(0, 0, sw, sh);
  const brightness = new Float32Array(sw * sh);
  const colorData = new Uint8Array(sw * sh * 3);
  for (let i = 0; i < sw * sh; i++) {
    const r = id.data[i * 4] ?? 0;
    const g = id.data[i * 4 + 1] ?? 0;
    const b = id.data[i * 4 + 2] ?? 0;
    brightness[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    colorData[i * 3] = r;
    colorData[i * 3 + 1] = g;
    colorData[i * 3 + 2] = b;
  }
  stretchBrightnessHistogram(brightness);
  return {
    brightness,
    colorData,
    sampleW: sw,
    sampleH: sh,
    width: img.width,
    height: img.height,
    dataURL,
  };
}

/**
 * Fetch an image URL and sample it into a `SampledImage`. Browser-only.
 * Throws if the URL fails to load or the canvas can't be acquired.
 */
export function loadImageFromUrl(url: string): Promise<SampledImage> {
  return new Promise<SampledImage>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('loadImageFromUrl: no document (SSR)'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = (): void => {
      try {
        resolve(sampleImageElement(img, url));
      } catch (err: unknown) {
        // SILENT-CATCH: error is re-propagated via Promise reject — caller's catch handles it
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };
    img.onerror = (): void => {
      reject(new Error(`loadImageFromUrl: failed to load ${url}`));
    };
    img.src = url;
  });
}
